import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  ButtonSection,
  Content,
  Form,
  FormBlock,
  Heading,
  ItemDiv,
  Label,
  LeftSection,
  SelectedText,
  SubHeading,
  TitleSection,
  TopSection,
  ViewSection,
  ViewTab,
  OuterDiv
} from './styles';
import Button from '../../../../../component/button';
import AddIconDropdown from '../../../../../component/viewCard/addIconDropdown';
import { rootStore } from '../../../../../mobx/rootStore';
import { Dropdown, DropdownItem } from '../../../../../component/dropdown';
import { CustomerDetailsInterface } from '../../../../../interfaces/CustomerInterface';
import DatePicker from '../../../../../component/dateTime';
import { CreateProjectStep2ViewItems , projectIcon } from '../../../../../global/row';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, TAB_BUTTON_TYPE , ICON_COLORS, PROJECT_ICON_TYPE } from '../../../../../global/constants';
import { dispatchProjectError } from '../../../../../services/projectServices';
import { useMobileDevice } from '../../../../../global/useMobile';
import { isEmpty } from '../../../../../helpers/common';
import Input from '../../../../../component/input';
import Tabs from '../../../../../component/tabs';
interface Props {
  onClickCreate: () => void;
}

const ProjectDetails: React.FC<Props> = (props: Props) => {
  const { onClickCreate } = props;
  // use state variables
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const [customerDetails, setCustomerDetails] = useState<CustomerDetailsInterface | null>(null);
  const [searchCustomer, setSearchCustomer] = useState('');
  const [isSearch, setIsSearch] = useState(false);
  // mobx variables
  const { projectStore, projectTemplateStore, customerStore } = rootStore;
  const { createProjectInput } = projectStore;
  const { startDate, dueDate } = createProjectInput;
  const { projectTemplates } = projectTemplateStore;
  const { customers } = customerStore;
  // other variables
  const isMobile = useMobileDevice();
  const customerDropdownRef = useRef<{
    closeDropdown: () => void;
  }>(null);

  // update customer
  const updateCustomer = useCallback(
    (customer: CustomerDetailsInterface) => {
      createProjectInput.setCustomerId(customer?.id);
      setCustomerDetails(customer);
    },
    [createProjectInput]
  );

  useEffect(() => {
    if (!isEmpty(createProjectInput?.customerId)) {
      const selectedCustomer = customers?.find((item) => item?.id === createProjectInput?.customerId);
      if (selectedCustomer) updateCustomer(selectedCustomer);
    }
    if (isEmpty(createProjectInput?.startDate)) createProjectInput?.setStartDate(new Date());
    // Set random icon and color if not already set
    if (!createProjectInput.icon) {
      // Exclude the first icon if it's just 'initial-text' (optional)
      const iconList = projectIcon.filter((icon) => icon.label && icon.label !== 'initial-text');
      const randomIcon = iconList[Math.floor(Math.random() * iconList.length)];
      const colorKeys = Object.keys(ICON_COLORS).map(Number);
      const randomColor = colorKeys[Math.floor(Math.random() * colorKeys.length)];
      createProjectInput.setIcon({
        type: PROJECT_ICON_TYPE.ICON,
        reference: randomIcon.label,
        color: randomColor
      });
    }
  }, [createProjectInput, createProjectInput?.customerId, customers, projectTemplates, updateCustomer]);

  // onChange search
  const onChangeSearch = useCallback((value: string) => {
    setIsSearch(true);
    setSearchCustomer(value);
  }, []);

  const handleCreate = useCallback(() => {
    if (isEmpty(createProjectInput?.name)) {
      dispatchProjectError('Please add project name');
      return;
    }
    onClickCreate();
  }, [createProjectInput, onClickCreate]);

  const onChangeStartDate = useCallback(
    (date: dayjs.Dayjs | null) => {
      if (dueDate && date?.toDate() && dueDate < date?.toDate()) {
        createProjectInput.setDueDate(null);
      }
      createProjectInput.setStartDate(date?.toDate() || null);
    },
    [createProjectInput, dueDate]
  );

  const onChangeEndDate = useCallback((date: dayjs.Dayjs | null) => {
    if (date === null) {
      rootStore.projectStore.createProjectInput.setDueDate(null);
    } else {
      rootStore.projectStore.createProjectInput.setDueDate(date.toDate());
    }
  }, []);

  const onChangeViewType = useCallback((viewType: number) => {
    if (!viewType) return;
    rootStore.projectStore.createProjectInput.setTaskFilter(viewType);
  }, []);

  // render customer dropdown
  const renderCustomerDropdown = useMemo(() => {
    const filteredCustomer = !isSearch
      ? customers
      : customers?.filter((x) => x?.value?.trim().toLowerCase().includes(searchCustomer.toLowerCase()));
    //when no customer found this will be shown
    if (!filteredCustomer || filteredCustomer.length === 0) {
      return <DropdownItem label='No customer found' />;
    }

    return filteredCustomer?.map((item) => {
      return (
        <DropdownItem
          key={item.id}
          label={item.value}
          onClick={(e) => {
            e?.stopPropagation();
            setIsSearch(false);
            setSearchCustomer('');
            updateCustomer(item);
            customerDropdownRef?.current?.closeDropdown();
          }}
        />
      );
    });
  }, [customers, isSearch, searchCustomer, updateCustomer]);

  // render view tabs
  const renderViewTabs = useMemo(() => {
    return CreateProjectStep2ViewItems.map((item) => {
      return (
        <Tabs
          key={item?.key}
          variant={TAB_BUTTON_TYPE.PILL}
          title={item?.label}
          iconName={item?.iconName}
          iconPosition='start'
          onClick={() => onChangeViewType(item?.key)}
          isActive={createProjectInput?.taskFilter?.taskViewType === item?.key}
        />
      );
    });
  }, [createProjectInput?.taskFilter?.taskViewType, onChangeViewType]);

  return (
    <>
      <form onSubmit={handleCreate}>
        <OuterDiv>
          <LeftSection>
            <TopSection>
              <TitleSection>
                {!isMobile && <Heading>Add project details</Heading>}
                <SubHeading>Fill in the information to create project.</SubHeading>
              </TitleSection>
              <Content>
                <AddIconDropdown iconSize={20} size={36} isCreateProject={true} />
                <Input
                  label='Project name'
                  placeHolder='Enter project name'
                  value={createProjectInput.name}
                  onChange={(e) => {
                    createProjectInput.setName(e.target.value);
                  }}
                />
                <Form>
                  <Label>Customer</Label>
                  <Dropdown
                    ref={customerDropdownRef}
                    modalTitle='Customer'
                    isMinWidth={398}
                    closeOnItemClick={false}
                    updateDropdownOpen={setIsCustomerDropdownOpen}
                    content={
                      <>
                        <Input
                          placeHolder={isEmpty(customerDetails?.companyName) ? 'Select a customer' : ''}
                          value={searchCustomer}
                          onChange={(e) => onChangeSearch(e.target.value)}
                          trailingIconName='down-arrow-icon'
                          trailingIconClassName={`angleDown-icon ${isCustomerDropdownOpen ? 'rotate' : ''}`}
                        />
                        {isEmpty(searchCustomer) && <SelectedText>{customerDetails?.companyName}</SelectedText>}
                      </>
                    }
                    trigger='click'>
                    <ItemDiv>{renderCustomerDropdown}</ItemDiv>
                  </Dropdown>
                </Form>
                <FormBlock>
                  <Form>
                    <Label>Start date</Label>
                    <DatePicker
                      label='Select start date'
                      value={startDate}
                      className='dueDate'
                      inputFormat='DD/MM/YYYY'
                      onChange={onChangeStartDate}
                    />
                  </Form>
                  <Form>
                    <Label>End date (Optional)</Label>
                    <DatePicker
                      label='Select end date'
                      value={dueDate ? dueDate : null}
                      className='dueDate'
                      inputFormat='DD/MM/YYYY'
                      minDate={startDate ?? new Date()}
                      onChange={onChangeEndDate}
                    />
                  </Form>
                </FormBlock>
                <ViewSection>
                  <Label>Views</Label>
                  <ViewTab>{renderViewTabs}</ViewTab>
                </ViewSection>
              </Content>
            </TopSection>
            <ButtonSection>
              <Button
                title='Create Project'
                variant={BUTTON_TYPE.PRIMARY}
                buttonType='submit'
                disabled={isEmpty(createProjectInput.name)}
              />
            </ButtonSection>
          </LeftSection>
        </OuterDiv>
      </form>
    </>
  );
};

export default observer(ProjectDetails);
